import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import {
  TAXONOMY_LEVEL_1,
  TAXONOMY_LEVEL_2,
  TAXONOMY_LEVEL_3,
} from 'product-landing-pages/utils/taxonomyLevels';
import QueryString from 'qs';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import {
  CONSUMER_SEARCH_BRANDS_PRESENTATION,
  CONSUMER_SEARCH_BRANDS_REDIRECTS,
  CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS,
  CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS,
} from 'brand/enum/consumerSearchRedirects';
import { slugify } from 'shared/utils/consumerSearchSlugify';
import { normalizeValue } from 'shared/utils/normalizeValue';

const CategorizedBrandPage = ({
  brandDisplayName,
  brandName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  consumerSearchFilters,
  displayName,
  globalPricingId,
  isCrawler = false,
  consumerSearchInitialData = '{}',
  pageBreadcrumbs,
}: IProductLandingPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      brandName={brandName}
      breadcrumbs={
        pageBreadcrumbs || [
          { name: startCase(category), slug: `/${category}` },
          {
            name: brandDisplayName,
            slug: `/brand/${slugify(brandName)}`,
          },
          {
            name: displayName,
            slug: `/brand/${slugify(brandName)}`,
            isStatic: true,
          },
        ]
      }
      browseKey={browseKey}
      browseValue={browseValue}
      category={category}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
      description={description}
      globalPricingId={globalPricingId}
      hasBreadcrumbSchema
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      displayName={name}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, req, res, locale }) => {
  const { pageSlug: category, brandName }: { pageSlug: string; brandName: string } = query;
  const q = QueryString.parse(query);
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbsUtils = await import('server/utils/breadcrumbs/getBreadcrumbs');

  const isCrawler = isSearchCrawler(req?.headers);

  try {
    const brandPresentationName: string =
      CONSUMER_SEARCH_BRANDS_PRESENTATION[brandName] || startCase(brandName);
    const categoryName: string = startCase(category);
    const brandBrowseName: string = startCase(brandName, {
      ignoreSpecialDisplay: true,
    }).toLowerCase();
    const normalizedBrandName = normalizeValue(brandBrowseName);

    // BUY3-1381: need to redirect special character brand names to normalized value
    // as Google is still crawling V1 GOAT
    if (normalizedBrandName !== brandBrowseName) {
      return {
        redirect: {
          destination: getRedirectionPath(`/${category}/brand/${normalizedBrandName}`, locale),
          permanent: true,
        },
      };
    }

    let consumerSearchFilters: any = {};
    if (TAXONOMY_LEVEL_1.includes(category.toLowerCase())) {
      consumerSearchFilters = {
        brands: brandName,
        categories: category,
      };
    } else if (TAXONOMY_LEVEL_2.includes(category.toLowerCase())) {
      consumerSearchFilters = {
        brands: brandName,
        productTypes: category,
      };
    } else if (TAXONOMY_LEVEL_3.includes(category.toLowerCase())) {
      consumerSearchFilters = {
        brands: brandName,
        activities: category,
      };
    }

    if ((consumerSearchFilters?.productTypes || '').toLowerCase() === 'sneakers') {
      delete consumerSearchFilters.productTypes;
      consumerSearchFilters.categories = 'footwear';
    }

    if (CONSUMER_SEARCH_BRANDS_REDIRECTS[brandName]) {
      return {
        redirect: {
          destination: getRedirectionPath(
            `/${category}/brand/${CONSUMER_SEARCH_BRANDS_REDIRECTS[brandName]}`,
            locale,
          ),
          permanent: false,
        },
      };
    }
    const breadcrumbs = await breadcrumbsUtils.getBreadcrumbs({
      req,
      res,
      path: `${category}/brand/${CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS[brandName] || brandName}`,
    });

    if (CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS[brandName]) {
      consumerSearchFilters.brands = CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS[brandName];
    }
    // If consumer search is enabled, we need to fetch the consumer data details
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: {
        ...q,
        pageFilters: consumerSearchFilters,
      },
    });

    if (!consumerSearchInitialData?.items?.hasData) {
      return { notFound: true };
    }

    const CORRECT_BRAND_SLUG = CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS[brandName] || brandName;

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        brandName,
        brandDisplayName: brandPresentationName,
        breadcrumbs,
        browseKey: 'brand',
        browseValue: brandBrowseName,
        category,
        constructorResponse: {},
        displayName: `${brandPresentationName} ${categoryName}`,
        globalPricingId,
        isCrawler,
        consumerSearchFilters,
        consumerSearchInitialData: JSON.stringify({}),
        pageBreadcrumbs: [
          {
            name: brandPresentationName,
            slug: `/brand/${CORRECT_BRAND_SLUG}`,
            isStatic: false,
          },
          {
            name: startCase(category),
            slug: `/${category}/brand/${CORRECT_BRAND_SLUG}`,
            isStatic: true,
          },
        ],
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default CategorizedBrandPage;
